import React, { useState } from "react";
import { Plus, Search } from "lucide-react";
import { Page } from "../library";
import { CompactSingleSelectDropdown, MetricCard } from "./TeacherMocksShared";
import TeacherMockRow from "./TeacherMockRow";
import TeacherDraftMockRow from "./TeacherDraftMockRow";
import TeacherMockCard from "./TeacherMockCard";
import TeacherMocksPagination from "./TeacherMocksPagination";
import CreateMockModal from "./CreateMockModal";
import ViewMockDrawer from "./ViewMockDrawer";
import TeacherRankingDrawer from "./TeacherRankingDrawer";
import PublishedMockModal from "./PublishedMockModal";
import { defaultForm, statusTabs } from "./teacherMocksConstants";

export default function TeacherMocksPage({
  activeStatus,
  onStatusChange,
  searchTerm,
  onSearchChange,
  courseId,
  onCourseChange,
  mocks,
  pagination,
  statusCounts,
  isLoadingMocks,
  mockTemplatesError,
  page,
  onPageChange,
  courseOptions,
  isLoadingCourses,
  togglingIds,
  rankingDrawer,
  publicationSuccess,
  onClosePublicationSuccess,
  isModalOpen,
  modalMode,
  isViewDrawerOpen,
  viewMockValues,
  isLoadingViewMock,
  onOpenCreateModal,
  onCloseMockModal,
  onCloseViewDrawer,
  onToggleEnabled,
  onViewMock,
  onCopyMock,
  onPublishDraft,
  lastCreatedId,
  onOpenMockLink,
  onViewRanking,
  onCloseRankingDrawer,
  onCreate,
  onAutosave,
  onUpdate,
}) {
  const [widgetFilter, setWidgetFilter] = useState(null);

  const lowEngagementMocks = mocks.filter((mock) =>
    mock.totalStudents && (mock.answered / mock.totalStudents) < 0.3
  );
  const lowAverageMocks = mocks.filter((mock) =>
    Number(mock.averagePercent.replace("%", "")) < 70
  );

  const lowEngagement = lowEngagementMocks.length;
  const lowAverage = lowAverageMocks.length;

  const filteredMocks = widgetFilter === "lowEngagement"
    ? lowEngagementMocks
    : widgetFilter === "lowAverage"
      ? lowAverageMocks
      : mocks;

  const toggleFilter = (filter) => setWidgetFilter((prev) => (prev === filter ? null : filter));

  const isDraftTab = activeStatus === "draft";

  return (
    <Page
      title="Simulados do professor"
      actions={
        <button type="button" className="tprof-create" onClick={onOpenCreateModal}>
          <Plus size={16} strokeWidth={1.33325} aria-hidden="true" />
          <span className="tprof-create-label">Criar simulado</span>
        </button>
      }
    >
      <div className="tprof-page">
        <div className="tprof-tabs" role="tablist" aria-label="Status dos simulados">
          {statusTabs.map((tab) => (
            <button
              key={tab.key}
              type="button"
              role="tab"
              aria-selected={activeStatus === tab.key}
              className={`tprof-tab ${activeStatus === tab.key ? "active" : ""}`}
              onClick={() => onStatusChange(tab.key)}
            >
              {tab.label}
              <span className="tprof-count">{statusCounts[tab.key] || 0}</span>
            </button>
          ))}
        </div>

        <div className="tprof-filters">
          <label className="tprof-search">
            <Search size={14} aria-hidden="true" />
            <input
              type="search"
              value={searchTerm}
              onChange={(event) => onSearchChange(event.target.value)}
              placeholder="Buscar por ID, curso ou nome..."
            />
          </label>

          <CompactSingleSelectDropdown
            value={courseId ? String(courseId) : ""}
            onChange={onCourseChange}
            options={courseOptions.map((item) => ({ value: String(item.id), label: item.name }))}
            placeholder="Todos os cursos"
          />
        </div>

        <div className="tprof-alert-grid">
          <MetricCard
            label="Baixo engajamento"
            value={lowEngagement}
            description="com participação abaixo de 30%"
            tooltip="Simulados em que menos de 30% dos alunos finalizaram: finalizados divididos pelo total de alunos."
            onClick={lowEngagement > 0 ? () => toggleFilter("lowEngagement") : undefined}
            isActive={widgetFilter === "lowEngagement"}
          />
          <MetricCard
            label="Média abaixo de 70%"
            value={lowAverage}
            description="com baixa performance"
            tooltip="Simulados cuja média percentual de acertos está abaixo de 70%."
            onClick={lowAverage > 0 ? () => toggleFilter("lowAverage") : undefined}
            isActive={widgetFilter === "lowAverage"}
          />
        </div>

        <div className="tprof-section-title">
          <span>Simulados</span>
          <span className="tprof-count">
            {widgetFilter ? filteredMocks.length : (pagination?.totalItems ?? mocks.length)}
          </span>
          {widgetFilter && (
            <span className="tprof-filter-badge">
              {widgetFilter === "lowEngagement" ? "Baixo engajamento" : "Baixa performance"}
            </span>
          )}
          {widgetFilter && (
            <button type="button" className="tprof-clear-filter" onClick={() => setWidgetFilter(null)}>
              × Limpar filtro
            </button>
          )}
        </div>

        {mockTemplatesError && (
          <div className="teacher-mocks-submit-error">{mockTemplatesError}</div>
        )}

        {filteredMocks.length > 0 && (
          <>
            <div className="tprof-table-wrap">
              <table className={`tprof-table${isDraftTab ? " tprof-table-drafts" : ""}`}>
                {isDraftTab ? (
                  <colgroup>
                    <col style={{ width: "40%" }} />
                    <col style={{ width: "14%" }} />
                    <col style={{ width: "14%" }} />
                    <col style={{ width: "8%" }} />
                    <col style={{ width: "24%" }} />
                  </colgroup>
                ) : (
                  <colgroup>
                    <col style={{ width: "26%" }} />
                    <col style={{ width: "6%" }} />
                    <col style={{ width: "9%" }} />
                    <col style={{ width: "8%" }} />
                    <col style={{ width: "10%" }} />
                    <col style={{ width: "10%" }} />
                    <col style={{ width: "9%" }} />
                    <col style={{ width: "22%" }} />
                  </colgroup>
                )}
                <thead>
                  {isDraftTab ? (
                    <tr>
                      <th>Simulado</th>
                      <th>ID</th>
                      <th>Criado em</th>
                      <th style={{ textAlign: "center" }}>Questões</th>
                      <th style={{ textAlign: "right" }}>Ações</th>
                    </tr>
                  ) : (
                    <tr>
                      <th>Simulado</th>
                      <th>ID</th>
                      <th>Criado em</th>
                      <th style={{ textAlign: "center" }}>Questões</th>
                      <th style={{ textAlign: "center" }}>Participantes</th>
                      <th style={{ textAlign: "center" }}>Média acertos</th>
                      <th style={{ textAlign: "center" }}>Tempo médio</th>
                      <th style={{ textAlign: "right" }}>Ações</th>
                    </tr>
                  )}
                </thead>
                <tbody>
                  {filteredMocks.map((mock) => (
                    isDraftTab ? (
                      <TeacherDraftMockRow
                        key={mock.id}
                        mock={mock}
                        onEdit={onViewMock}
                        onPublish={onPublishDraft}
                        isHighlighted={mock.id === lastCreatedId}
                      />
                    ) : (
                      <TeacherMockRow
                        key={mock.id}
                        mock={mock}
                        onToggleEnabled={onToggleEnabled}
                        onOpenLink={onOpenMockLink}
                        onView={onViewMock}
                        onCopy={onCopyMock}
                        onRanking={onViewRanking}
                        isToggling={togglingIds.has(mock.id)}
                        isHighlighted={mock.id === lastCreatedId}
                      />
                    )
                  ))}
                </tbody>
              </table>
            </div>

            <div className="tprof-mobile-list">
              {filteredMocks.map((mock) => (
                <TeacherMockCard
                  key={mock.id}
                  mock={mock}
                  onToggleEnabled={onToggleEnabled}
                  isToggling={togglingIds.has(mock.id)}
                  onOpenLink={onOpenMockLink}
                  onView={onViewMock}
                  onCopy={onCopyMock}
                  onRanking={onViewRanking}
                />
              ))}
            </div>
          </>
        )}

        {isLoadingMocks && (
          <div className="teacher-mocks-empty">
            Carregando simulados...
          </div>
        )}

        {!isLoadingMocks && filteredMocks.length === 0 && (
          <div className="teacher-mocks-empty">
            {widgetFilter
              ? "Nenhum simulado encontrado para este filtro."
              : "Nenhum simulado encontrado para os filtros atuais."}
          </div>
        )}

        {!widgetFilter && pagination?.totalPages > 1 && (
          <TeacherMocksPagination
            page={pagination.page || page}
            totalPages={pagination.totalPages}
            onPageChange={onPageChange}
          />
        )}
      </div>

      <ViewMockDrawer
        open={isViewDrawerOpen}
        onClose={onCloseViewDrawer}
        mock={viewMockValues}
        isLoading={isLoadingViewMock}
      />
      <CreateMockModal
        courseOptions={courseOptions}
        isLoadingCourses={isLoadingCourses}
        open={isModalOpen}
        onClose={onCloseMockModal}
        onCreate={onCreate}
        onAutosave={onAutosave}
        onUpdate={onUpdate}
        mode={modalMode}
        initialValues={modalMode === "draft" ? viewMockValues : defaultForm}
        isLoading={modalMode === "draft" && isLoadingViewMock}
      />
      <TeacherRankingDrawer
        open={rankingDrawer.open}
        mock={rankingDrawer.mock}
        ranking={rankingDrawer.ranking}
        loading={rankingDrawer.loading}
        error={rankingDrawer.error}
        onClose={onCloseRankingDrawer}
      />
      <PublishedMockModal
        publication={publicationSuccess}
        onClose={onClosePublicationSuccess}
      />
    </Page>
  );
}
